import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@material-ui/core';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  FileCopy as CopyIcon
} from '@material-ui/icons';
import RagService from '../services/RagService';
import notify from "../utils/notifier";

// Generate a session ID that persists for the conversation
const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  statusCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  chatContainer: {
    height: '60vh', // Fixed height instead of flex: 1
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  messagesContainer: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
    maxHeight: '40vh'
  },
  messageBox: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    maxWidth: '80%'
  },
  userMessage: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    marginLeft: 'auto'
  },
  assistantMessage: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`
  },
  inputContainer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    gap: theme.spacing(1)
  },
  inputField: {
    flex: 1
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  searchResults: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius
  }
}));

function RagChat() {
  const classes = useStyles();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  // Search functionality removed - backend endpoint not implemented yet

  // Auto-scroll removed - proper container sizing eliminates need for scrolling

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const loadSystemStatus = async () => {
    try {
      const status = await RagService.getRagStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await RagService.chatWithRag(inputValue, sessionId);
      
      // Add debugging to see what we receive
      console.log('RAG Response received:', response);
      
      // Check if response has the expected format
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from RAG service');
      }

      // Handle double-encoded JSON in response.answer field
      let processedResponse = response;
      
      // Check if answer field contains JSON string (common issue with Claude responses)
      if (typeof response.answer === 'string') {
        const answerContent = response.answer.trim();
        if (answerContent.startsWith('{') && answerContent.endsWith('}')) {
          try {
            console.log('Attempting to parse answer field as JSON...');
            console.log('Answer content:', answerContent);
            const parsedAnswer = JSON.parse(answerContent);
            if (parsedAnswer && typeof parsedAnswer === 'object' && parsedAnswer.answer) {
              console.log('Successfully parsed double-encoded JSON from answer field');
              console.log('Parsed answer:', parsedAnswer);
              processedResponse = parsedAnswer;
            }
          } catch (e) {
            console.log('Answer field looks like JSON but failed to parse:', e);
            console.log('Raw answer content for debugging:', JSON.stringify(answerContent));
            // Keep original response if parsing fails
          }
        }
      }
      
      // Additional check: if description contains the error message, try to clear it when we have valid parsed data
      if (processedResponse !== response && response.description === "Response received but couldn't parse as structured JSON. Displaying as plain text.") {
        // We successfully parsed the JSON, so clear the error message
        console.log('Clearing backend error message since we successfully parsed JSON on frontend');
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: processedResponse.answer || 'No answer provided',
        description: processedResponse.description || '',
        endpoints: processedResponse.endpoints || [],
        code_examples: processedResponse.code_examples || null,
        links: processedResponse.links || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      // notify.success("Response received"); // Remove notification to reduce noise
    } catch (error) {
      console.error('Chat failed:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Error: ${error.response?.data?.message || error.message || 'Unknown error occurred'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Search functionality removed - will be added when backend endpoint is implemented

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyContent = (content, type = 'command') => {
    try {
      navigator.clipboard.writeText(content);
      notify({ type: 'info', message: `${type} copied to clipboard!` });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      notify({ type: 'info', message: `${type} copied to clipboard!` });
    }
  };

  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    const isError = message.type === 'error';

    // Extract JSON code blocks from message content for copy functionality
    const extractJsonFromContent = (content) => {
      if (typeof content !== 'string') return null;
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      return jsonMatch ? jsonMatch[1].trim() : null;
    };
    
    const jsonContent = extractJsonFromContent(message.content);
    
    return (
      <Box
        key={message.id}
        className={`${classes.messageBox} ${isUser ? classes.userMessage : classes.assistantMessage}`}
        style={{
          backgroundColor: isError ? '#ffebee' : undefined,
          borderColor: isError ? '#f44336' : undefined
        }}
      >
        <Typography variant="body1" style={{ marginBottom: 8 }}>
          {message.content}
        </Typography>

        {jsonContent && (
          <Box mt={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" color="textSecondary">Response Example:</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" bgcolor="grey.100" style={{ borderRadius: 4, padding: 8 }}>
              <Box style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', overflow: 'auto', flex: 1 }}>
                {jsonContent}
              </Box>
              <IconButton 
                size="small" 
                onClick={() => handleCopyContent(jsonContent, 'JSON response')}
                style={{ marginLeft: 8, padding: 4 }}
                title="Copy JSON response"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        
        {message.description && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
            {message.description}
          </Typography>
        )}
        
        {message.code_examples && message.code_examples.curl && (
          <Box mt={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" color="textSecondary">cURL Examples:</Typography>
            </Box>
            {Array.isArray(message.code_examples.curl) ? (
              // Multiple curl commands
              message.code_examples.curl.map((curlCmd, idx) => (
                <Box key={idx} mt={1}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" bgcolor="grey.100" style={{ borderRadius: 4, padding: 8 }}>
                    <Box style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', overflow: 'auto', flex: 1 }}>
                      {curlCmd}
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyContent(curlCmd, 'cURL command')}
                      style={{ marginLeft: 8, padding: 4 }}
                      title="Copy cURL command"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              // Single curl command
              <Box mt={1}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" bgcolor="grey.100" style={{ borderRadius: 4, padding: 8 }}>
                  <Box style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', overflow: 'auto', flex: 1 }}>
                    {message.code_examples.curl}
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyContent(message.code_examples.curl, 'cURL command')}
                    style={{ marginLeft: 8, padding: 4 }}
                    title="Copy cURL command"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {message.endpoints && message.endpoints.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">Relevant Endpoints:</Typography>
            {message.endpoints.map((endpoint, idx) => (
              <Chip 
                key={idx} 
                label={`${endpoint.method} ${endpoint.url}`} 
                size="small" 
                style={{ margin: '4px' }}
              />
            ))}
          </Box>
        )}
        
        {message.links && message.links.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">References:</Typography>
            {message.links.map((link, idx) => (
              <Chip 
                key={idx} 
                label={link} 
                size="small" 
                clickable
                onClick={() => window.open(link, '_blank')}
                style={{ margin: '4px' }}
              />
            ))}
          </Box>
        )}
        
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
          {message.timestamp.toLocaleTimeString()}
        </Typography>
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h5">Foundation Service Documentation Assistant</Typography>
        <div className={classes.actionButtons}>
          <Tooltip title="Refresh System Status">
            <IconButton onClick={loadSystemStatus} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="System Information">
            <IconButton onClick={() => {
              if (systemStatus) {
                alert(`System Status: ${systemStatus.is_ready ? 'Ready' : 'Not Ready'}\nDocuments: ${systemStatus.documents_count || 0}\nLast Updated: ${systemStatus.last_updated || 'Never'}`);
              } else {
                alert('System status not available');
              }
            }} size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* System Status */}
      {systemStatus && (
        <Card className={classes.statusCard}>
          <CardContent style={{ padding: '12px' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" style={{ marginRight: '16px' }}>
                Assistant Status
              </Typography>
              <Chip 
                label={systemStatus.is_ready ? "Ready" : "Not Ready"} 
                color={systemStatus.is_ready ? "primary" : "secondary"}
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Chat Container */}
      <Paper className={classes.chatContainer}>
        {/* Messages */}
        <div className={classes.messagesContainer}>
          {messages.length === 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body1" color="textSecondary">
                👋 Hi! I'm your documentation assistant.
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Ask me anything about Document Service APIs, templates, or features.
              </Typography>
            </Box>
          )}
          
          {messages.map(renderMessage)}
          
          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Thinking...
              </Typography>
            </Box>
          )}
        </div>

        {/* Search functionality removed - will be added when backend endpoint is ready */}

        {/* Input Area */}
        <div className={classes.inputContainer}>
          <TextField
            className={classes.inputField}
            placeholder="Ask about document service APIs..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            multiline
            maxRows={3}
            disabled={isLoading}
          />
          
          {/* Search button removed - functionality not yet implemented */}
          
          <Tooltip title="Send Message">
            <IconButton 
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              color="primary"
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </div>
      </Paper>
    </div>
  );
}

export default RagChat;